import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import PhoneInput from '@/components/PhoneInput';
import { normalizePhone } from '@/lib/phoneUtils';
import {
  Plus,
  Trash2,
  Save,
  Loader2,
  CheckCircle2,
  MessageCircle,
  Phone,
  RefreshCw,
  Send,
  Eye,
  EyeOff,
  Wifi,
  X,
  Pencil,
  Copy,
} from 'lucide-react';

const EMPTY_FORM = { api_key: '', app_id: '', app_name: '', source_phone: '' };
const VARIABLE_SOURCE_OPTIONS = [
  { value: 'lead_name', label: 'Nombre del lead' },
  { value: 'lead_property', label: 'Propiedad por la que pidió información' },
  { value: 'lead_vendor', label: 'Vendedor asignado' },
  { value: 'custom', label: 'Texto personalizado (libre)' },
];

const extractVariableIndexes = (textValue) => {
  const text = typeof textValue === 'string' ? textValue : JSON.stringify(textValue || '');
  const regex = /\{\{(\d+)\}\}/g;
  const found = new Set();
  let match = regex.exec(text);
  while (match) {
    found.add(Number(match[1]));
    match = regex.exec(text);
  }
  return [...found].sort((a, b) => a - b);
};

const templateBodyText = (tpl) => {
  const body = tpl?.body ?? tpl?.data ?? '';
  return typeof body === 'string' ? body : JSON.stringify(body || '');
};

const templateIdentity = (tpl, idx) => {
  return String(tpl?.template_id || tpl?.id || tpl?.elementName || tpl?.name || `tpl-${idx}`);
};

const defaultSourceByIndex = (index) => {
  if (index === 1) return 'lead_name';
  if (index === 2) return 'lead_property';
  if (index === 3) return 'lead_vendor';
  return 'custom';
};

const makeDefaultMapping = (indexes) => {
  return indexes.map((index) => ({
    index,
    source: defaultSourceByIndex(index),
    custom_value: null,
  }));
};

const mergeMappingWithIndexes = (indexes, mapping = []) => {
  const mapByIndex = new Map((Array.isArray(mapping) ? mapping : []).map((m) => [Number(m.index), m]));
  return indexes.map((index) => {
    const current = mapByIndex.get(index);
    if (current) {
      return {
        index,
        source: current.source || 'custom',
        custom_value: current.custom_value ?? null,
      };
    }
    return {
      index,
      source: defaultSourceByIndex(index),
      custom_value: null,
    };
  });
};

const placeholderBySource = (source) => {
  if (source === 'lead_name') return 'Ej: Juan Pérez';
  if (source === 'lead_property') return 'Ej: Depto 302 Torre A';
  if (source === 'lead_vendor') return 'Ej: María González';
  return 'Escribe el valor';
};

const renderHighlightedBody = (body) => {
  const parts = String(body || '').split(/(\{\{\d+\}\})/g);
  return parts.map((part, idx) => {
    if (/^\{\{\d+\}\}$/.test(part)) {
      return <mark key={`${part}-${idx}`} className="px-1 rounded bg-amber-500/20 text-amber-300">{part}</mark>;
    }
    return <span key={`${part}-${idx}`}>{part}</span>;
  });
};

export default function GupshupWabaTab() {
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [addDialog, setAddDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const [manageDialog, setManageDialog] = useState(false);
  const [activeConfig, setActiveConfig] = useState(null);
  const [step, setStep] = useState(1);
  const [editingConfig, setEditingConfig] = useState(false);
  const [editForm, setEditForm] = useState({ api_key: '', app_id: '', app_name: '', source_phone: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editShowKey, setEditShowKey] = useState(false);

  const [remoteTemplates, setRemoteTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState(new Set());
  const [templateMappings, setTemplateMappings] = useState({});
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplSaving, setTplSaving] = useState(false);

  const [selectedSavedTemplateId, setSelectedSavedTemplateId] = useState('');
  const [testParamValues, setTestParamValues] = useState({});
  const [testPhone, setTestPhone] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [lastSendLog, setLastSendLog] = useState(null);
  const [sendLogTab, setSendLogTab] = useState('request');

  const loadNumbers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/whatsapp/gupshup/numbers');
      const nums = Array.isArray(r.data?.numbers) ? r.data.numbers : [];
      // Debug diagnóstico eliminación de números WABA
      // eslint-disable-next-line no-console
      console.log('WABA numbers:', nums.map((n) => ({ id: n?.id, _id: n?._id })));
      const filtered = nums.filter((n) => n?.id);
      if (filtered.length !== nums.length) {
        // eslint-disable-next-line no-console
        console.warn("Algunos WABA configs no tienen campo 'id'");
      }
      // eslint-disable-next-line no-console
      console.log('[LOAD NUMBERS]', filtered.length, 'números cargados');
      setNumbers(filtered);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar la lista de numeros');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNumbers();
  }, [loadNumbers]);

  const addNumber = async () => {
    if (!form.api_key.trim() || !form.app_id.trim() || !form.app_name.trim() || !form.source_phone.trim()) {
      toast.error('Todos los campos son requeridos');
      return;
    }
    setSaving(true);
    try {
      await api.post('/whatsapp/gupshup/numbers', {
        api_key: form.api_key,
        app_id: form.app_id,
        app_name: form.app_name,
        source_phone: normalizePhone(form.source_phone, '52', 'gupshup'),
      });
      toast.success('Numero agregado correctamente');
      setAddDialog(false);
      setForm(EMPTY_FORM);
      setShowKey(false);
      await loadNumbers();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      let message = 'No se pudo guardar el numero';
      if (typeof raw === 'string' && raw) {
        message = raw;
      } else if (raw && typeof raw === 'object') {
        const field = raw.field || 'App ID';
        const company = raw.conflict_company_name || 'otra empresa';
        message = `Este ${field} ya está registrado en la cuenta: ${company}. Si es un error, contacta a soporte.`;
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const deleteNumber = async (id) => {
    // eslint-disable-next-line no-console
    console.log('[DELETE WABA] iniciando, id=', id);
    if (!id) {
      // eslint-disable-next-line no-console
      console.error('[DELETE WABA] id undefined');
      toast.error('No se encontró identificador del número');
      return;
    }
    if (!window.confirm('¿Eliminar este numero y sus plantillas asociadas?')) {
      // eslint-disable-next-line no-console
      console.log('[DELETE WABA] cancelado por usuario, id=', id);
      return;
    }

    const previousNumbers = [...numbers];
    try {
      const res = await api.delete(`/whatsapp/gupshup/numbers/${id}`);
      // eslint-disable-next-line no-console
      console.log('[DELETE WABA] respuesta=', res?.data);
      toast.success('Número eliminado correctamente');
      setNumbers((prev) => prev.filter((n) => n.id !== id));
      if (activeConfig?.id === id) {
        setManageDialog(false);
        setActiveConfig(null);
      }
      await loadNumbers();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[DELETE WABA] error=', e);
      toast.error(e.response?.data?.detail || 'Error al eliminar');
      setNumbers(previousNumbers);
      await loadNumbers();
    }
  };

  const openManage = async (config) => {
    setActiveConfig(config);
    setStep(1);
    setEditingConfig(false);
    setEditShowKey(false);
    setEditForm({
      api_key: '',
      app_id: config?.app_id || config?.app_name || '',
      app_name: config?.app_name || '',
      source_phone: config?.source_phone || '',
    });
    setRemoteTemplates([]);
    setSelectedTemplates(new Set());
    setTemplateMappings({});
    setSelectedSavedTemplateId('');
    setTestParamValues({});
    setTestPhone('');
    setLastSendLog(null);
    setSendLogTab('request');
    setManageDialog(true);
    try {
      const r = await api.get(`/whatsapp/gupshup/saved-templates/${config.id}`);
      const templates = Array.isArray(r.data?.templates) ? r.data.templates : [];
      setSavedTemplates(templates);
      if (templates.length > 0) {
        setSelectedSavedTemplateId(templates[0].id || templates[0].template_id || templates[0].name);
      }
    } catch {
      setSavedTemplates([]);
    }
  };

  const saveConfigEdits = async () => {
    if (!activeConfig?.id) return;
    if (!editForm.app_id?.trim() || !editForm.app_name?.trim() || !editForm.source_phone?.trim()) {
      toast.error('App ID, App Name y teléfono fuente son obligatorios');
      return;
    }
    setEditSaving(true);
    try {
      const r = await api.post('/whatsapp/gupshup/numbers', {
        id: activeConfig.id,
        api_key: editForm.api_key,
        app_id: editForm.app_id,
        app_name: editForm.app_name,
        source_phone: normalizePhone(editForm.source_phone, '52', 'gupshup'),
      });
      setActiveConfig(r.data);
      setEditingConfig(false);
      setEditShowKey(false);
      setEditForm((prev) => ({ ...prev, api_key: '' }));
      await loadNumbers();
      toast.success('Configuración actualizada correctamente');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo actualizar la configuración');
    } finally {
      setEditSaving(false);
    }
  };

  const copyText = async (text, successMessage) => {
    try {
      await navigator.clipboard.writeText(String(text || ''));
      toast.success(successMessage);
    } catch {
      toast.error('No se pudo copiar al portapapeles');
    }
  };

  const fetchTemplates = async () => {
    if (!activeConfig?.id) {
      toast.error('Config invalida: falta identificador del numero');
      return;
    }
    setTplLoading(true);
    try {
      const r = await api.get('/whatsapp/gupshup/templates', {
        params: { config_id: activeConfig.id },
        skipAuthRedirect: true,
      });
      const list = Array.isArray(r.data?.templates) ? r.data.templates : [];
      setRemoteTemplates(list);
      setSelectedTemplates(new Set(list.map((_, i) => i)));

      const seedMappings = {};
      list.forEach((tpl, idx) => {
        const body = templateBodyText(tpl);
        const indexes = extractVariableIndexes(body);
        const key = templateIdentity(tpl, idx);
        seedMappings[key] = makeDefaultMapping(indexes);
      });
      setTemplateMappings(seedMappings);

      setStep(2);
      toast.success(`Plantillas remotas obtenidas: ${list.length}`);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error('API Key invalida o App ID incorrecto');
      } else {
        toast.error(e.response?.data?.detail || 'No se pudieron obtener plantillas');
      }
    } finally {
      setTplLoading(false);
    }
  };

  const toggleTemplate = (index) => {
    setSelectedTemplates((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const updateTemplateMapping = (tpl, idx, variableIndex, patch) => {
    const key = templateIdentity(tpl, idx);
    const body = templateBodyText(tpl);
    const indexes = extractVariableIndexes(body);
    setTemplateMappings((prev) => {
      const current = mergeMappingWithIndexes(indexes, prev[key] || []);
      const next = current.map((item) => {
        if (item.index !== variableIndex) return item;
        return {
          ...item,
          ...patch,
          custom_value: patch.source && patch.source !== 'custom'
            ? null
            : (patch.custom_value ?? item.custom_value ?? null),
        };
      });
      return { ...prev, [key]: next };
    });
  };

  const toggleAll = () => {
    if (selectedTemplates.size === remoteTemplates.length) {
      setSelectedTemplates(new Set());
      return;
    }
    setSelectedTemplates(new Set(remoteTemplates.map((_, i) => i)));
  };

  const saveTemplates = async () => {
    if (!activeConfig?.id) return;
    const picked = remoteTemplates
      .map((tpl, idx) => ({ tpl, idx }))
      .filter(({ idx }) => selectedTemplates.has(idx))
      .map(({ tpl, idx }) => {
        const key = templateIdentity(tpl, idx);
        const body = templateBodyText(tpl);
        const indexes = extractVariableIndexes(body);
        const mapping = mergeMappingWithIndexes(indexes, templateMappings[key] || []);
        return {
          ...tpl,
          variable_mapping: mapping,
        };
      });
    setTplSaving(true);
    try {
      await api.post('/whatsapp/gupshup/templates', {
        config_id: activeConfig.id,
        templates: picked,
      });
      const r = await api.get(`/whatsapp/gupshup/saved-templates/${activeConfig.id}`);
      const templates = Array.isArray(r.data?.templates) ? r.data.templates : [];
      setSavedTemplates(templates);
      if (templates.length > 0) {
        setSelectedSavedTemplateId(templates[0].id || templates[0].template_id || templates[0].name);
      }
      setStep(3);
      await loadNumbers();
      toast.success(`Plantillas guardadas: ${picked.length}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar la seleccion');
    } finally {
      setTplSaving(false);
    }
  };

  const selectedSavedTemplate = useMemo(() => {
    return savedTemplates.find((tpl) => String(tpl.id || tpl.template_id || tpl.name) === String(selectedSavedTemplateId)) || null;
  }, [savedTemplates, selectedSavedTemplateId]);

  const selectedTemplateBody = templateBodyText(selectedSavedTemplate);
  const selectedTemplateIndexes = useMemo(() => extractVariableIndexes(selectedTemplateBody), [selectedTemplateBody]);
  const selectedTemplateMapping = useMemo(
    () => mergeMappingWithIndexes(selectedTemplateIndexes, selectedSavedTemplate?.variable_mapping || []),
    [selectedSavedTemplate, selectedTemplateIndexes],
  );

  useEffect(() => {
    if (!selectedSavedTemplate) {
      setTestParamValues({});
      return;
    }
    const initial = {};
    selectedTemplateMapping.forEach((item) => {
      if (item.source === 'custom' && item.custom_value) {
        initial[String(item.index)] = item.custom_value;
      } else {
        initial[String(item.index)] = '';
      }
    });
    setTestParamValues(initial);
  }, [selectedSavedTemplate, selectedTemplateMapping]);

  const previewBody = useMemo(() => {
    return selectedTemplateBody.replace(/\{\{(\d+)\}\}/g, (_, idx) => {
      const val = testParamValues[String(idx)] ?? '';
      return val || `{{${idx}}}`;
    });
  }, [selectedTemplateBody, testParamValues]);

  const sendTest = async () => {
    if (!activeConfig?.id) return;
    if (!testPhone.trim()) {
      toast.error('Ingresa un numero destino para la prueba');
      return;
    }
    if (!selectedSavedTemplate) {
      toast.error('Selecciona una plantilla para prueba');
      return;
    }

    const templateId = selectedSavedTemplate.template_id;
    if (!templateId) {
      toast.error('La plantilla seleccionada no tiene UUID de Gupshup (template_id)');
      return;
    }

    const params = selectedTemplateIndexes.map((idx) => String(testParamValues[String(idx)] || '').trim());
    const missingIdx = selectedTemplateIndexes.find((idx, i) => !params[i]);
    if (missingIdx) {
      toast.error(`Completa el valor para {{${missingIdx}}} antes de enviar`);
      return;
    }

    setTestSending(true);
    try {
      const r = await api.post('/whatsapp/gupshup/test', {
        config_id: activeConfig.id,
        destination: testPhone,
        template_id: templateId,
        params,
      }, { skipAuthRedirect: true });
      setLastSendLog({
        timestamp: new Date().toLocaleTimeString(),
        curl_equivalent: r.data?.curl_equivalent || '',
        gupshup_status_code: r.data?.gupshup_status_code,
        gupshup_response: r.data?.gupshup_response,
        request_debug: r.data?.request_debug,
      });
      setSendLogTab('request');
      toast.success(r.data?.message || 'Mensaje de prueba encolado');
    } catch (e) {
      if (e.response?.data) {
        setLastSendLog({
          timestamp: new Date().toLocaleTimeString(),
          curl_equivalent: e.response.data?.curl_equivalent || '',
          gupshup_status_code: e.response.data?.gupshup_status_code || e.response.status,
          gupshup_response: e.response.data?.gupshup_response || e.response.data,
          request_debug: e.response.data?.request_debug,
        });
        setSendLogTab('response');
      }
      const code = e.response?.status;
      if (code === 400) toast.error('Destino, plantilla o parámetros inválidos');
      else if (code === 401) toast.error('API Key inválida');
      else if (code === 429) toast.warning('Demasiadas solicitudes, intenta mas tarde');
      else toast.error(e.response?.data?.message || e.response?.data?.detail || 'Error al enviar prueba');
    } finally {
      setTestSending(false);
    }
  };

  return (
    <>
      <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="gupshup-waba-main-card">
        <CardHeader className="pb-4 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading text-base text-white flex items-center gap-2" data-testid="gupshup-waba-title">
              <MessageCircle className="w-4 h-4 text-green-400" />
              WhatsApp Gupshup (WABA)
            </CardTitle>
            <CardDescription className="text-muted-foreground text-xs" data-testid="gupshup-waba-subtitle">
              Administra multiples numeros WABA, sincroniza plantillas y prueba conectividad en vivo.
            </CardDescription>
          </div>
          <Button
            onClick={() => setAddDialog(true)}
            className="bg-green-600 hover:bg-green-500 text-xs"
            data-testid="gupshup-waba-add-number-button"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />Agregar Numero
          </Button>
        </CardHeader>
        <CardContent className="space-y-3" data-testid="gupshup-waba-content">
          {loading ? (
            <div className="flex items-center justify-center py-10" data-testid="gupshup-waba-loading-state">
              <Loader2 className="w-5 h-5 animate-spin text-green-400" />
            </div>
          ) : numbers.length === 0 ? (
            <div className="text-center py-8" data-testid="gupshup-waba-empty-state">
              <Phone className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground" data-testid="gupshup-waba-empty-title">No hay numeros configurados</p>
              <p className="text-[11px] text-muted-foreground/80" data-testid="gupshup-waba-empty-description">Agrega tu primer numero WABA para comenzar a sincronizar plantillas.</p>
            </div>
          ) : (
            numbers.map((n) => (
              <div
                key={n.id}
                className="p-3 rounded-lg border border-white/10 bg-black/20"
                data-testid={`gupshup-waba-number-row-${n.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap" data-testid={`gupshup-waba-number-header-${n.id}`}>
                      <span className="text-sm text-white font-mono" data-testid={`gupshup-waba-number-phone-${n.id}`}>{n.source_phone}</span>
                      <Badge variant="outline" className="text-[10px] border-white/10" data-testid={`gupshup-waba-number-app-${n.id}`}>{n.app_name || 'Sin app'}</Badge>
                      {n.templates_synced ? (
                        <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px] border-0" data-testid={`gupshup-waba-number-sync-badge-${n.id}`}>
                          {n.templates_count || 0} plantillas
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] border-amber-500/20 text-amber-400" data-testid={`gupshup-waba-number-pending-badge-${n.id}`}>
                          Sin sincronizar
                        </Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground font-mono" data-testid={`gupshup-waba-number-key-${n.id}`}>
                      Key: {n.api_key_masked || '********'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10"
                      onClick={() => openManage(n)}
                      data-testid={`gupshup-waba-manage-button-${n.id}`}
                    >
                      <Wifi className="w-3.5 h-3.5 mr-1" />Gestionar
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-red-500/20 text-red-400 hover:bg-red-500/10"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // eslint-disable-next-line no-console
                        console.log('[DELETE BTN] clicked, id=', n?.id, 'item=', n);
                        deleteNumber(n?.id);
                      }}
                      data-testid={`gupshup-waba-delete-button-${n.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />Eliminar
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="sm:max-w-md" data-testid="gupshup-waba-add-dialog">
          <DialogHeader>
            <DialogTitle data-testid="gupshup-waba-add-dialog-title">Agregar Numero WABA</DialogTitle>
            <DialogDescription data-testid="gupshup-waba-add-dialog-description">
              Registra API Key, App ID, App Name y Source Phone para habilitar sincronizacion y envios de prueba.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3" data-testid="gupshup-waba-add-dialog-content">
            <div className="space-y-2">
              <Label htmlFor="gupshup-api-key-input">API Key</Label>
              <div className="relative">
                <Input
                  id="gupshup-api-key-input"
                  data-testid="gupshup-waba-add-api-key-input"
                  type={showKey ? 'text' : 'password'}
                  value={form.api_key}
                  onChange={(e) => setForm((s) => ({ ...s, api_key: e.target.value }))}
                  placeholder="Tu API key de Gupshup"
                  className="pr-10 font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowKey((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                  data-testid="gupshup-waba-add-api-key-toggle"
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="gupshup-app-id-input">App ID</Label>
              <Input
                id="gupshup-app-id-input"
                data-testid="gupshup-waba-add-app-id-input"
                value={form.app_id}
                onChange={(e) => setForm((s) => ({ ...s, app_id: e.target.value }))}
                placeholder="ID de tu App en Gupshup"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gupshup-app-name-input">App Name (src.name)</Label>
              <Input
                id="gupshup-app-name-input"
                data-testid="gupshup-waba-add-app-name-input"
                value={form.app_name}
                onChange={(e) => setForm((s) => ({ ...s, app_name: e.target.value }))}
                placeholder="Ej: TestLeadCEM"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gupshup-source-phone-input">Source Phone</Label>
              <PhoneInput
                data-testid="gupshup-waba-add-source-phone-input"
                value={form.source_phone}
                onChange={(v) => setForm((s) => ({ ...s, source_phone: v }))}
              />
            </div>
            <Button
              onClick={addNumber}
              disabled={saving}
              className="w-full bg-green-600 hover:bg-green-500"
              data-testid="gupshup-waba-add-save-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Guardar Numero
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={manageDialog} onOpenChange={setManageDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="gupshup-waba-manage-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between" data-testid="gupshup-waba-manage-dialog-title">
              <span>Gestionar Numero WABA</span>
              <button
                type="button"
                onClick={() => setManageDialog(false)}
                className="text-muted-foreground hover:text-white"
                data-testid="gupshup-waba-manage-close-button"
              >
                <X className="w-4 h-4" />
              </button>
            </DialogTitle>
            <DialogDescription data-testid="gupshup-waba-manage-dialog-description">
              Flujo de 3 pasos: estado de sincronización, selección con mapeo de variables y prueba en vivo.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap gap-2" data-testid="gupshup-waba-stepper">
            {[1, 2, 3].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStep(s)}
                className={`px-3 py-1.5 rounded-full text-xs border transition ${step === s ? 'bg-green-500/20 text-green-400 border-green-500/40' : step > s ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' : 'bg-black/20 text-muted-foreground border-white/10'}`}
                data-testid={`gupshup-waba-step-button-${s}`}
              >
                Paso {s}
              </button>
            ))}
          </div>

          {step === 1 && (
            <div className="space-y-4" data-testid="gupshup-waba-step-1-panel">
              <div className="p-3 rounded-lg border border-white/10 bg-black/20" data-testid="gupshup-waba-step-1-config-card">
                <div className="flex items-center justify-between gap-2">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground" data-testid="gupshup-waba-step-1-phone">Telefono: <span className="text-white font-mono">{activeConfig?.source_phone || '-'}</span></p>
                    <p className="text-xs text-muted-foreground" data-testid="gupshup-waba-step-1-app-id">App ID: <span className="text-white">{activeConfig?.app_id || '-'}</span></p>
                    <p className="text-xs text-muted-foreground" data-testid="gupshup-waba-step-1-app-name">App Name / src.name: <span className="text-white">{activeConfig?.app_name || '-'}</span></p>
                    <p className="text-xs text-muted-foreground" data-testid="gupshup-waba-step-1-key">Key: <span className="text-white font-mono">{activeConfig?.api_key_masked || '-'}</span></p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-cyan-500/20 text-cyan-300 hover:bg-cyan-500/10"
                    onClick={() => setEditingConfig((v) => !v)}
                    data-testid="gupshup-waba-step-1-edit-config-button"
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1" />Editar configuración
                  </Button>
                </div>
                {activeConfig?.templates_synced ? (
                  <p className="text-xs text-emerald-400" data-testid="gupshup-waba-step-1-sync-ok">✓ {activeConfig?.templates_count || 0} plantillas sincronizadas</p>
                ) : (
                  <p className="text-xs text-amber-400" data-testid="gupshup-waba-step-1-sync-pending">Sin sincronizar aún</p>
                )}
              </div>

              {editingConfig && (
                <div className="p-3 rounded-lg border border-cyan-500/20 bg-cyan-500/5 space-y-3" data-testid="gupshup-waba-step-1-edit-form">
                  <div className="space-y-2">
                    <Label htmlFor="gupshup-step1-edit-api-key">API Key</Label>
                    <div className="relative">
                      <Input
                        id="gupshup-step1-edit-api-key"
                        type={editShowKey ? 'text' : 'password'}
                        value={editForm.api_key}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, api_key: e.target.value }))}
                        placeholder={activeConfig?.api_key_masked || '********'}
                        className="pr-10 font-mono"
                        data-testid="gupshup-waba-step-1-edit-api-key-input"
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                        onClick={() => setEditShowKey((v) => !v)}
                        data-testid="gupshup-waba-step-1-edit-api-key-toggle"
                      >
                        {editShowKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-[11px] text-muted-foreground" data-testid="gupshup-waba-step-1-edit-api-key-hint">Si lo dejas vacío, se conserva la API Key actual.</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gupshup-step1-edit-appid">App ID</Label>
                    <Input
                      id="gupshup-step1-edit-appid"
                      value={editForm.app_id}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, app_id: e.target.value }))}
                      className="font-mono"
                      data-testid="gupshup-waba-step-1-edit-app-id-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gupshup-step1-edit-appname">App Name (src.name)</Label>
                    <Input
                      id="gupshup-step1-edit-appname"
                      value={editForm.app_name}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, app_name: e.target.value }))}
                      placeholder="Ej: TestLeadCEM"
                      className="font-mono"
                      data-testid="gupshup-waba-step-1-edit-app-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gupshup-step1-edit-source">Teléfono fuente</Label>
                    <PhoneInput
                      data-testid="gupshup-waba-step-1-edit-source-phone-input"
                      value={editForm.source_phone}
                      onChange={(v) => setEditForm((prev) => ({ ...prev, source_phone: v }))}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      onClick={saveConfigEdits}
                      disabled={editSaving}
                      className="bg-cyan-600 hover:bg-cyan-500"
                      data-testid="gupshup-waba-step-1-edit-save-button"
                    >
                      {editSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Guardar cambios
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingConfig(false);
                        setEditShowKey(false);
                        setEditForm({
                          api_key: '',
                          app_id: activeConfig?.app_id || activeConfig?.app_name || '',
                          app_name: activeConfig?.app_name || '',
                          source_phone: activeConfig?.source_phone || '',
                        });
                      }}
                      data-testid="gupshup-waba-step-1-edit-cancel-button"
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              <Button
                onClick={fetchTemplates}
                disabled={tplLoading}
                className="w-full bg-emerald-600 hover:bg-emerald-500"
                data-testid="gupshup-waba-step-1-go-step-2-button"
              >
                {tplLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Ir a Plantillas
              </Button>

              {savedTemplates.length > 0 && (
                <div className="space-y-2" data-testid="gupshup-waba-step-1-saved-templates-block">
                  <p className="text-xs text-white">Plantillas guardadas actualmente</p>
                  <div className="max-h-40 overflow-y-auto space-y-1 rounded-lg border border-white/10 bg-black/20 p-2" data-testid="gupshup-waba-step-1-saved-templates-list">
                    {savedTemplates.map((tpl) => (
                      <div key={tpl.id} className="text-[11px] flex items-center justify-between gap-2" data-testid={`gupshup-waba-saved-template-row-${tpl.id}`}>
                        <span className="font-mono text-white truncate">{tpl.name}</span>
                        <div className="flex items-center gap-1">
                          <Badge variant="outline" className="text-[9px] border-white/10">{tpl.language || 'es'}</Badge>
                          <Badge variant="outline" className="text-[9px] border-white/10">{tpl.status || 'PENDING'}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="gupshup-waba-step-2-panel">
              <div className="flex items-center justify-between" data-testid="gupshup-waba-step-2-header">
                <p className="text-sm text-white" data-testid="gupshup-waba-step-2-count">Plantillas remotas: {remoteTemplates.length}</p>
                <Button variant="outline" size="sm" onClick={toggleAll} data-testid="gupshup-waba-step-2-toggle-all-button">
                  {selectedTemplates.size === remoteTemplates.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
                </Button>
              </div>

              <div className="max-h-[45vh] overflow-y-auto space-y-2" data-testid="gupshup-waba-step-2-list">
                {remoteTemplates.length === 0 ? (
                  <div className="text-center py-6 border border-dashed border-white/10 rounded-lg space-y-2" data-testid="gupshup-waba-step-2-empty">
                    <p className="text-xs text-muted-foreground">No hay plantillas remotas cargadas.</p>
                    <Button onClick={fetchTemplates} variant="outline" size="sm" data-testid="gupshup-waba-step-2-load-remote-button">
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Cargar plantillas remotas
                    </Button>
                  </div>
                ) : (
                  remoteTemplates.map((tpl, idx) => {
                    const checked = selectedTemplates.has(idx);
                    const status = String(tpl.status || '').toUpperCase();
                    const bodyText = templateBodyText(tpl);
                    const indexes = extractVariableIndexes(bodyText);
                    const key = templateIdentity(tpl, idx);
                    const mergedMapping = mergeMappingWithIndexes(indexes, templateMappings[key] || []);
                    return (
                      <div
                        key={`${tpl.elementName || tpl.name || 'template'}-${idx}`}
                        className={`p-2.5 rounded-lg border cursor-pointer transition ${checked ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-white/10 bg-black/20 hover:border-white/20'}`}
                        onClick={() => toggleTemplate(idx)}
                        data-testid={`gupshup-waba-step-2-template-row-${idx}`}
                      >
                        <div className="flex items-start gap-2">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleTemplate(idx)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`gupshup-waba-step-2-template-checkbox-${idx}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap mb-1" data-testid={`gupshup-waba-step-2-template-meta-${idx}`}>
                              <span className="text-xs text-white font-mono truncate" data-testid={`gupshup-waba-step-2-template-name-${idx}`}>{tpl.elementName || tpl.name || `template_${idx + 1}`}</span>
                              <Badge variant="outline" className="text-[9px] border-white/10">{tpl.category || 'MARKETING'}</Badge>
                              <Badge variant="outline" className="text-[9px] border-white/10">{tpl.languageCode || tpl.language || 'es'}</Badge>
                              <Badge className={`text-[9px] border-0 ${status === 'APPROVED' ? 'bg-emerald-500/20 text-emerald-400' : status === 'REJECTED' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>{status || 'PENDING'}</Badge>
                            </div>
                            <p className="text-[11px] text-muted-foreground line-clamp-2" data-testid={`gupshup-waba-step-2-template-body-${idx}`}>
                              {typeof bodyText === 'string' ? bodyText : JSON.stringify(bodyText)}
                            </p>

                            {checked && indexes.length > 0 && (
                              <div
                                className="mt-2 p-2 rounded-lg border border-white/10 bg-black/20 space-y-2"
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`gupshup-waba-step-2-mapping-box-${idx}`}
                              >
                                <p className="text-[11px] text-white" data-testid={`gupshup-waba-step-2-mapping-title-${idx}`}>Mapeo de variables detectadas ({indexes.length})</p>
                                {mergedMapping.map((mapItem) => (
                                  <div key={`${key}-${mapItem.index}`} className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid={`gupshup-waba-step-2-mapping-row-${idx}-${mapItem.index}`}>
                                    <Label className="text-[11px] text-amber-300">{`{{${mapItem.index}}}`}</Label>
                                    <select
                                      className="bg-black/30 border border-white/10 rounded-md px-2 py-1 text-xs"
                                      value={mapItem.source}
                                      onChange={(e) => updateTemplateMapping(tpl, idx, mapItem.index, { source: e.target.value })}
                                      data-testid={`gupshup-waba-step-2-mapping-source-${idx}-${mapItem.index}`}
                                    >
                                      {VARIABLE_SOURCE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                      ))}
                                    </select>
                                    {mapItem.source === 'custom' ? (
                                      <Input
                                        value={mapItem.custom_value || ''}
                                        onChange={(e) => updateTemplateMapping(tpl, idx, mapItem.index, { custom_value: e.target.value })}
                                        placeholder="Texto personalizado"
                                        className="h-8 text-xs"
                                        data-testid={`gupshup-waba-step-2-mapping-custom-${idx}-${mapItem.index}`}
                                      />
                                    ) : (
                                      <p className="text-[11px] text-muted-foreground self-center" data-testid={`gupshup-waba-step-2-mapping-label-${idx}-${mapItem.index}`}>
                                        {VARIABLE_SOURCE_OPTIONS.find((opt) => opt.value === mapItem.source)?.label}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="flex items-center justify-between" data-testid="gupshup-waba-step-2-footer">
                <p className="text-xs text-muted-foreground" data-testid="gupshup-waba-step-2-selected-count">Seleccionadas: {selectedTemplates.size}</p>
                <Button
                  onClick={saveTemplates}
                  disabled={tplSaving || remoteTemplates.length === 0}
                  className="bg-emerald-600 hover:bg-emerald-500"
                  data-testid="gupshup-waba-step-2-save-button"
                >
                  {tplSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Guardar {selectedTemplates.size} Plantillas
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="gupshup-waba-step-3-panel">
              <div className="p-3 rounded-lg border border-emerald-500/20 bg-emerald-500/10 text-emerald-300 text-sm flex items-center gap-2" data-testid="gupshup-waba-step-3-success-banner">
                <CheckCircle2 className="w-4 h-4" />
                {savedTemplates.length} plantillas sincronizadas.
              </div>

              <div className="space-y-2" data-testid="gupshup-waba-step-3-template-selector-wrap">
                <Label htmlFor="gupshup-step3-template-selector">Seleccionar plantilla para prueba</Label>
                <select
                  id="gupshup-step3-template-selector"
                  className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm"
                  value={selectedSavedTemplateId}
                  onChange={(e) => setSelectedSavedTemplateId(e.target.value)}
                  data-testid="gupshup-waba-step-3-template-selector"
                >
                  <option value="">Selecciona una plantilla</option>
                  {savedTemplates.map((tpl) => {
                    const value = String(tpl.id || tpl.template_id || tpl.name);
                    return (
                      <option key={value} value={value}>
                        {`${tpl.name || 'sin_nombre'} — ${tpl.category || 'N/A'} — ${tpl.language || 'N/A'}`}
                      </option>
                    );
                  })}
                </select>
              </div>

              {selectedSavedTemplate && (
                <>
                  <div className="p-3 rounded-lg border border-white/10 bg-black/20 space-y-2" data-testid="gupshup-waba-step-3-template-raw">
                    <p className="text-xs text-white">Body de plantilla (variables resaltadas)</p>
                    <p className="text-[12px] text-muted-foreground whitespace-pre-wrap" data-testid="gupshup-waba-step-3-template-raw-body">
                      {renderHighlightedBody(selectedTemplateBody)}
                    </p>
                  </div>

                  {selectedTemplateIndexes.length > 0 && (
                    <div className="space-y-2" data-testid="gupshup-waba-step-3-variables-form">
                      <p className="text-xs text-white">Valores de variables para prueba</p>
                      {selectedTemplateIndexes.map((index) => {
                        const mapping = selectedTemplateMapping.find((m) => Number(m.index) === Number(index));
                        const source = mapping?.source || 'custom';
                        return (
                          <div key={`test-var-${index}`} className="space-y-1" data-testid={`gupshup-waba-step-3-variable-row-${index}`}>
                            <Label className="text-[11px] text-amber-300">{`{{${index}}}`} ({VARIABLE_SOURCE_OPTIONS.find((opt) => opt.value === source)?.label || 'Libre'})</Label>
                            <Input
                              value={testParamValues[String(index)] || ''}
                              onChange={(e) => setTestParamValues((prev) => ({ ...prev, [String(index)]: e.target.value }))}
                              placeholder={placeholderBySource(source)}
                              className="font-mono"
                              data-testid={`gupshup-waba-step-3-variable-input-${index}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="p-3 rounded-lg border border-cyan-500/20 bg-cyan-500/5 space-y-2" data-testid="gupshup-waba-step-3-preview-box">
                    <p className="text-xs text-cyan-300">Preview en vivo</p>
                    <p className="text-[12px] text-cyan-100 whitespace-pre-wrap" data-testid="gupshup-waba-step-3-preview-body">{previewBody}</p>
                  </div>
                </>
              )}

              <div className="space-y-2" data-testid="gupshup-waba-step-3-test-form">
                <Label htmlFor="gupshup-test-phone">Numero destino para prueba</Label>
                <Input
                  id="gupshup-test-phone"
                  data-testid="gupshup-waba-step-3-test-phone-input"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="Ej. 5215512345678"
                  className="font-mono"
                />
                <p className="text-[11px] text-muted-foreground" data-testid="gupshup-waba-step-3-test-hint">Formato recomendado: solo digitos con lada pais.</p>
              </div>

              <Button
                onClick={sendTest}
                disabled={testSending}
                className="w-full bg-green-600 hover:bg-green-500"
                data-testid="gupshup-waba-step-3-send-test-button"
              >
                {testSending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Enviar Mensaje de Prueba
              </Button>

              {lastSendLog && (
                <div className="p-3 rounded-lg border border-white/10 bg-black/30 space-y-3" data-testid="gupshup-waba-step-3-last-log-box">
                  <p className="text-xs text-white" data-testid="gupshup-waba-step-3-last-log-title">
                    ── Log del último envío — {lastSendLog.timestamp} ──────────────────────
                  </p>
                  <div className="flex items-center gap-2" data-testid="gupshup-waba-step-3-last-log-tabs">
                    <button
                      type="button"
                      onClick={() => setSendLogTab('request')}
                      className={`px-2.5 py-1.5 rounded-md text-xs border ${sendLogTab === 'request' ? 'border-green-500/40 text-green-300 bg-green-500/10' : 'border-white/10 text-muted-foreground'}`}
                      data-testid="gupshup-waba-step-3-log-tab-request"
                    >
                      Request (cURL)
                    </button>
                    <button
                      type="button"
                      onClick={() => setSendLogTab('response')}
                      className={`px-2.5 py-1.5 rounded-md text-xs border ${sendLogTab === 'response' ? 'border-cyan-500/40 text-cyan-300 bg-cyan-500/10' : 'border-white/10 text-muted-foreground'}`}
                      data-testid="gupshup-waba-step-3-log-tab-response"
                    >
                      Response
                    </button>
                  </div>

                  {sendLogTab === 'request' ? (
                    <div className="space-y-2" data-testid="gupshup-waba-step-3-log-request-panel">
                      <pre className="text-[11px] bg-black/40 rounded-md p-3 overflow-x-auto text-emerald-200 border border-white/10" data-testid="gupshup-waba-step-3-log-request-pre">
                        {lastSendLog.curl_equivalent || 'Sin cURL equivalente disponible'}
                      </pre>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyText(lastSendLog.curl_equivalent, 'cURL copiado')}
                        data-testid="gupshup-waba-step-3-log-copy-request-button"
                      >
                        <Copy className="w-3.5 h-3.5 mr-1" />Copiar cURL
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2" data-testid="gupshup-waba-step-3-log-response-panel">
                      <p className="text-xs text-cyan-300" data-testid="gupshup-waba-step-3-log-response-status">
                        HTTP {lastSendLog.gupshup_status_code || 'N/A'}
                      </p>
                      <pre className="text-[11px] bg-black/40 rounded-md p-3 overflow-x-auto text-cyan-100 border border-white/10" data-testid="gupshup-waba-step-3-log-response-pre">
                        {JSON.stringify(lastSendLog.gupshup_response ?? {}, null, 2)}
                      </pre>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyText(JSON.stringify(lastSendLog.gupshup_response ?? {}, null, 2), 'Respuesta copiada')}
                        data-testid="gupshup-waba-step-3-log-copy-response-button"
                      >
                        <Copy className="w-3.5 h-3.5 mr-1" />Copiar respuesta
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="p-3 rounded-lg border border-white/10 bg-black/20 text-[11px] text-muted-foreground" data-testid="gupshup-waba-step-3-http-reference">
                <p className="text-xs text-white mb-1">Referencia de codigos esperados:</p>
                <p>200/202: encolado exitoso</p>
                <p>400: destino, plantilla o parámetros inválidos</p>
                <p>401: API key invalida</p>
                <p>429: rate limit</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
